/* eslint-disable react/prop-types */
import SubmitContext from 'context/submit/submitContext'
import PageContext from 'context/page/pageContext'
import React, { useContext, useEffect, useState } from 'react'
import Switch from 'react-switch'
import { Col, Row, FormGroup, Input, Alert } from 'reactstrap'
import { Controller, ErrorMessage, useFormContext } from 'react-hook-form'
import { useFormUtils } from 'views/shared/useFormUtils'
import { packagingInitialState } from './constants'
import BreakfastCerealProduct from 'models/BreakfastCerealProduct'
import { isNotNumericDecimal, isNotNumeric } from 'context/Utilities'

const BreakfastCerealNutrition = () => {
  const submitContext = useContext(SubmitContext)
  const pageContext = useContext(PageContext)

  const { current, setCurrent, updating } = submitContext
  const { setScrollToTop } = pageContext

  const [packaging, setPackaging] = useState(packagingInitialState)

  const methods = useFormContext()

  const { setSwitch, onChange, asterisk } = useFormUtils()

  useEffect(() => {
    setScrollToTop(true)
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (current?.category === packagingInitialState.category) {
      const {
        category,
        subCategory,
        Kcals,
        Folate,
        Fiber,
        WholeGrainCert,
        Iron,
        Sucrose,
        Rounding,
        UnderIronLimit,
        UnderSucroseLimit,
        ContainsNoSweeteners,
      } = current

      setPackaging({
        category,
        subCategory,
        Kcals,
        Folate,
        Fiber,
        WholeGrainCert: WholeGrainCert ?? false,
        Iron,
        Sucrose,
        Rounding: Rounding ?? false,
        UnderIronLimit: UnderIronLimit ?? false,
        UnderSucroseLimit: UnderSucroseLimit ?? false,
        ContainsNoSweeteners: ContainsNoSweeteners ?? false,
      })
    } else if (!updating) {
      setCurrent(BreakfastCerealProduct)
    }

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current])

  return (
    <Row>
      <Col xs={3}>
        <FormGroup>
          <p>
            <strong>Kcals per 100g dry cereal {asterisk}</strong>
          </p>
          <Controller
            as={Input}
            type="number"
            step="1"
            name="Kcals"
            onKeyDown={e => {
              if (isNotNumeric(e.key)) {
                e.preventDefault()
              }
            }}
            control={methods.control}
            rules={{
              maxLength: {
                value: 3,
                message: '⚠ Kcals exceeds 3 characters.',
              },
              required: {
                value: true,
                message: '⚠ Please enter Kcals per 100g dry cereal.',
              },
            }}
            onChange={([event]) => {
              onChange(event)
              return event
            }}
          />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="Kcals"
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
        </FormGroup>
        <FormGroup>
          <p>
            <strong>Folate (mcg) per 100g dry cereal {asterisk}</strong>
          </p>
          <Controller
            as={Input}
            type="number"
            step="0.01"
            name="Folate"
            onKeyDown={e => {
              if (isNotNumericDecimal(e.key)) {
                e.preventDefault()
              }
            }}
            control={methods.control}
            rules={{
              maxLength: {
                value: 6,
                message:
                  '⚠ Folate (mcg) per 100g dry cereal exceeds 6 characters.',
              },
              required: {
                value: true,
                message: '⚠ Please enter Folate (mg) per 100g dry cereal.',
              },
              pattern: {
                value: /^\d+(\.\d{1,2})?$/,
                message: '⚠ Maximum two decimal places allowed.',
              },
            }}
            onChange={([event]) => {
              onChange(event)
              return event
            }}
          />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="Folate"
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
        </FormGroup>
        <FormGroup>
          <p>
            <strong>Fiber (g) per 100g dry cereal {asterisk}</strong>
          </p>
          <Controller
            as={Input}
            type="number"
            step="0.01"
            name="Fiber"
            onKeyDown={e => {
              if (isNotNumericDecimal(e.key)) {
                e.preventDefault()
              }
            }}
            control={methods.control}
            rules={{
              maxLength: {
                value: 6,
                message:
                  '⚠ Fiber (g) per 100g dry cereal exceeds 6 characters.',
              },
              required: {
                value: true,
                message: '⚠ Please enter Fiber (g) per 100g dry cereal.',
              },
              pattern: {
                value: /^\d+(\.\d{1,2})?$/,
                message: '⚠ Maximum two decimal places allowed.',
              },
            }}
            onChange={([event]) => {
              onChange(event)
              return event
            }}
          />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="Fiber"
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
        </FormGroup>
      </Col>
      <Col xs={3}>
        <FormGroup>
          <p>
            <strong>Iron (mg) per 100g dry cereal {asterisk}</strong>
          </p>
          <Controller
            as={Input}
            type="number"
            step="0.01"
            name="Iron"
            placeholder={'Min 28mg iron per 100g dry cereal'}
            onKeyDown={e => {
              if (isNotNumericDecimal(e.key)) {
                e.preventDefault()
              }
            }}
            control={methods.control}
            rules={{
              maxLength: {
                value: 6,
                message:
                  '⚠ Iron (mg) per 100g dry cereal exceeds 6 characters.',
              },
              min: {
                value: 28,
                message:
                  '⚠ Iron (mg) per 100g dry cereal is below the 28mg minimum allowed.',
              },
              required: {
                value: true,
                message: '⚠ Please enter Iron (mg) per 100g dry cereal.',
              },
              pattern: {
                value: /^\d+(\.\d{1,2})?$/,
                message: '⚠ Maximum two decimal places allowed.',
              },
            }}
            onChange={([event]) => {
              onChange(event)
              return event
            }}
          />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="Iron"
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
        </FormGroup>
        <br />
        <FormGroup>
          <p>
            <strong>
              I certify that this product contains at least 28mg iron per 100
              grams of dry cereal. {asterisk}
            </strong>
          </p>
          <Controller
            as={Switch}
            name="UnderIronLimit"
            id="UnderIronLimit"
            onChange={([checked, event, id]) => {
              setSwitch(checked, event, id)
              return checked
            }}
            control={methods.control}
            defaultValue={packaging.UnderIronLimit}
            rules={{
              validate: UnderIronLimit => UnderIronLimit === true,
            }}
          />
          <br />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="UnderIronLimit"
              message={
                '⚠ You must certify that this product contains at least 28mg iron per 100 grams of dry cereal.'
              }
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
        </FormGroup>
        <br />
        <hr />
        <FormGroup>
          <p>
            <strong>Using FDA rounding rule for iron</strong>
          </p>
          <Controller
            as={Switch}
            name="Rounding"
            id="Rounding"
            onChange={([checked, event, id]) => {
              setSwitch(checked, event, id)
              return checked
            }}
            control={methods.control}
            defaultValue={packaging.Rounding}
          />
        </FormGroup>
      </Col>
      <Col xs={3}>
        <FormGroup>
          <p>
            <strong>Added Sugar (g) per 100g dry cereal {asterisk}</strong>
          </p>
          <Controller
            as={Input}
            type="number"
            step="0.01"
            name="Sucrose"
            placeholder={'Max 21.2g added sugar'}
            onKeyDown={e => {
              if (isNotNumericDecimal(e.key)) {
                e.preventDefault()
              }
            }}
            control={methods.control}
            rules={{
              maxLength: {
                value: 6,
                message:
                  '⚠ Added Sugar (g) per 100g dry cereal exceeds 6 characters.',
              },
              max: {
                value: 21.2,
                message:
                  '⚠ Added Sugar (g) per 100g dry cereal is above the 21.2g maximum allowed.',
              },
              required: {
                value: true,
                message: '⚠ Please enter Added Sugar (g) per 100g dry cereal.',
              },
              pattern: {
                value: /^\d+(\.\d{1,2})?$/,
                message: '⚠ Maximum two decimal places allowed.',
              },
            }}
            onChange={([event]) => {
              onChange(event)
              return event
            }}
          />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="Sucrose"
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
        </FormGroup>
        <br />
        <FormGroup>
          <p>
            <strong>
              I certify that this product contains no more than 21.2g added
              sugars per 100 grams of dry cereal. {asterisk}
            </strong>
          </p>
          <Controller
            as={Switch}
            name="UnderSucroseLimit"
            id="UnderSucroseLimit"
            onChange={([checked, event, id]) => {
              setSwitch(checked, event, id)
              return checked
            }}
            control={methods.control}
            defaultValue={packaging.UnderSucroseLimit}
            rules={{
              validate: UnderSucroseLimit => UnderSucroseLimit === true,
            }}
          />
          <br />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="UnderSucroseLimit"
              message={
                '⚠ You must certify that this product contains no more than 21.2g sucrose and/or other sugars per 100 grams of dry cereal.'
              }
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
        </FormGroup>
        <br />
        <hr />
        <FormGroup>
          <p>
            <strong>
              I certify that this product does not contain artificial
              sweeteners, reduced-calorie sweeteners, or no-calorie sweeteners.{' '}
              {asterisk}
            </strong>
          </p>
          <Controller
            as={Switch}
            name="ContainsNoSweeteners"
            id="ContainsNoSweeteners"
            onChange={([checked, event, id]) => {
              setSwitch(checked, event, id)
              return checked
            }}
            control={methods.control}
            defaultValue={packaging.ContainsNoSweeteners}
            rules={{
              validate: ContainsNoSweeteners => ContainsNoSweeteners === true,
            }}
          />
          <br />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="ContainsNoSweeteners"
              message={
                '⚠ You must certify that this product does not contain artificial sweeteners, reduced-calorie sweeteners, or no-calorie sweeteners.'
              }
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
        </FormGroup>
      </Col>
      <Col xs={3}>
        <FormGroup>
          <p>
            <strong>
              I certify that this product has a whole grain as the first
              ingredient. {asterisk}
            </strong>
          </p>
          <Controller
            as={Switch}
            name="WholeGrainCert"
            id="WholeGrainCert"
            onChange={([checked, event, id]) => {
              setSwitch(checked, event, id)
              return checked
            }}
            control={methods.control}
            defaultValue={packaging.WholeGrainCert}
            rules={{
              validate: WholeGrainCert => WholeGrainCert === true,
            }}
          />
          <br />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="WholeGrainCert"
              message={
                '⚠ You must certify that this product has a whole grain as the first ingredient.'
              }
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
        </FormGroup>
      </Col>
    </Row>
  )
}

export default BreakfastCerealNutrition
